/* eslint-disable react/no-unstable-nested-components, @next/next/no-img-element, sonarjs/cognitive-complexity, react/forbid-component-props */
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import React, { useState, useContext } from 'react';
import { useSelector } from 'react-redux';
import { Element } from 'react-scroll';
import { SCROLL_CONSTANTS } from '../../../../brands/default/constants';
import { DEAL_SINGLE_SELECT_MODAL } from '../../../../brands/default/content/components/deal';
import { WISHLIST } from '../../../../brands/default/features';
import { trackEvent } from '../../../../helpers/analytics';
import { isLightPage } from '../../../../helpers/browser';
import { formatCurrency } from '../../../../helpers/currency';
import {
  getModalSocialCue,
  useSocialCues,
  isDepositDeal,
  usePins,
  showPostage,
  showPostageSecondCheckout,
} from '../../../../helpers/deals';
import { replaceImageServer } from '../../../../helpers/image';
import { getAddonsFromDealPosition } from '../../../../helpers/secondCheckout';
import { getBackendSocialCueMessage } from '../../../../helpers/socialCues';
import ThemeContext from '../../../../providers/ThemeProvider';
import PrimaryButton from '../../../_generic/button/PrimaryButton';
import RadioButtonWithLabel from '../../../_generic/radio-button/RadioButtonWithLabel';
import WowMap from '../../../_generic/wow-map/WowMap';
import ListMainDeal from '../../ListMainDeal';
import MainDealBoughtCount from '../../MainDealBoughtCount';
import MainDealCarousel from '../../MainDealCarousel';
import MainDealFinePrint from '../../MainDealFinePrint';
import MainDealFullDetails from '../../MainDealFullDetails';
import SocialCueMainDeal from '../../SocialCueMainDeal';
import DealPriceSplat from '../DealPriceSplat';
import PostageAndPackagingText from '../PostageAndPackagingText';
import SectionTitle from '../SectionTitle';
import VatText from '../VatText';
import GiftingButton from '../gifting-button/GiftingButton';
import DealBuyButton from './DealBuyButton';
import DealGiftButton from './DealGiftButton';

const WishlistIcon = dynamic(() => import('../WishlistIcon'), {
  ssr: false,
});
export const layoutOptions = {
  travel: 'travel',
};

const DealSingleSelectModal = ({
  buttonRef = undefined,
  deal = {},
  finePrintCollapsed,
  fullDetailsCollapsed,
  handleBuyButtonClick,
  layout,
  secondCheckout = false,
  setFinePrintCollapsed,
  setFullDetailsCollapsed,
  mapView = false,
}) => {
  const theme = useContext(ThemeContext);
  const ssr = useSelector((state) => state.deals.ssr);
  const [payDeposit, setPayDeposit] = useState(deal?.depositAvailable || false); // deposit or payment in full
  const socialCuesData = useSocialCues(deal.id);
  const pins = usePins(deal);
  const isLayoutTravel = Boolean(layout === layoutOptions.travel);
  const hasPostage = secondCheckout
    ? showPostageSecondCheckout(deal)
    : showPostage(deal, true, true);

  const handleClick = () => {
    if (mapView) {
      handleBuyButtonClick();
    } else {
      trackEvent('buy-now-modal');
      handleBuyButtonClick(getAddonsFromDealPosition(deal, 0));
    }
  };

  const ProceedButton = () => {
    return (
      <>
        {
          <PrimaryButton
            aria-label={DEAL_SINGLE_SELECT_MODAL.buyNowButtonNoExclamation}
            className="size-larger full-width margin modal-sticky"
            onClick={handleClick}
          >
            {DEAL_SINGLE_SELECT_MODAL.buyNowButtonNoExclamation}
          </PrimaryButton>
        }
      </>
    );
  };

  const PayAmount = () => {
    if (!isDepositDeal(deal)) return false;

    return (
      <div className="cyo-deposit">
        <RadioButtonWithLabel
          checked={payDeposit}
          className={classNames('radio-button--boxed radio-button--switch', {
            'radio-button--green': payDeposit,
          })}
          label={
            <>
              <p className="label-text">
                {DEAL_SINGLE_SELECT_MODAL.payDeposit}
              </p>
              <p className="label-price">
                {formatCurrency(deal?.currency, deal?.depositPrice)}
              </p>
            </>
          }
          onChange={() => setPayDeposit(true)}
        />
        <RadioButtonWithLabel
          checked={!payDeposit}
          className={classNames('radio-button--boxed radio-button--switch', {
            'radio-button--green': !payDeposit,
          })}
          label={
            <>
              <p className="label-text">
                {DEAL_SINGLE_SELECT_MODAL.payFullDeposit}
              </p>
              <p className="label-price">
                {formatCurrency(deal?.currency, deal?.price)}
              </p>
            </>
          }
          onChange={() => setPayDeposit(false)}
        />
      </div>
    );
  };

  const SocialCues = () => {
    return (
      <div className="choose-your-options__cue">
        <SocialCueMainDeal
          className="main-deal-social-cue--assure main-deal-social-cue--no-margin"
          cue={
            deal.display?.bought && deal.totalBought === 0
              ? deal.newDealMessageTag
              : null
          }
        />
        <SocialCueMainDeal
          className="main-deal-social-cue--alert"
          cue={getModalSocialCue({ deal })}
          isStarMarked
        />
        <SocialCueMainDeal
          className="main-deal-social-cue--assure"
          cue={getBackendSocialCueMessage(socialCuesData)}
        />
      </div>
    );
  };

  const getGiftButton = () => {
    if (!deal.giftable) return null;

    return (
      <div className="dealgift-button">
        <DealGiftButton className="full-width outline margin" deal={deal} />
      </div>
    );
  };

  return (
    <>
      {isLayoutTravel && (
        <div className="cyo-block">
          <div className="cyo-header">
            {DEAL_SINGLE_SELECT_MODAL.chooseYourOptions}
          </div>
          <div className="cyo-body">
            <PayAmount />
            <SocialCues />
            <div className="cyo-buttons">
              <ProceedButton />
              {
                <DealBuyButton
                  buttonRef={buttonRef}
                  className={
                    'size-larger deal-main-deal__buy full-width margin'
                  }
                  deal={deal}
                  payDeposit={payDeposit}
                  text={DEAL_SINGLE_SELECT_MODAL.buyNowButtonNoExclamation}
                  trackingEnabled={false}
                />
              }
              {getGiftButton()}
            </div>
          </div>
        </div>
      )}
      {!isLayoutTravel && (
        <>
          {!isDepositDeal(deal) && (
            <div className="choose-your-options__image-container">
              {mapView && (
                <MainDealCarousel
                  deal={deal}
                  disableClickAction
                  isClosed={false}
                  onClick={() => {}}
                />
              )}
              {!mapView && (
                <img
                  alt={deal.headline}
                  className="choose-your-options__image"
                  src={replaceImageServer({
                    dealId: deal.id,
                    image: deal?.images[0],
                  })}
                />
              )}
              <DealPriceSplat
                className="bottom-right"
                deal={deal}
                discount={!mapView}
                secondCheckout
              />
              {mapView && WISHLIST && <WishlistIcon dealId={deal.id} />}
            </div>
          )}
          <h2 className="deal-headline">{deal.headline}</h2>
          {!mapView && (
            <PostageAndPackagingText
              className="mobile-invert"
              deal={deal}
              isMain
              isSmall
              secondCheckout={secondCheckout}
              show={hasPostage}
            />
          )}
          <SocialCues />
          <MainDealBoughtCount deal={deal} />
          <div className="deal-info">
            {deal?.discount > 0 && deal?.display?.discount && (
              <p className="money-item">
                <span className="label">
                  {DEAL_SINGLE_SELECT_MODAL.wasText}
                </span>
                <span className="value-strike">
                  {formatCurrency(
                    deal?.currency,
                    deal?.originalPrice,
                    false,
                    true,
                  )}
                </span>
              </p>
            )}
            <p className="money-item">
              <span className="label">{DEAL_SINGLE_SELECT_MODAL.now}</span>
              <span className="value">
                {formatCurrency(deal?.currency, deal.price, false, true)}
              </span>
            </p>
          </div>

          <GiftingButton deal={deal} isEvergreen={false} isMobile />
          <p
            className={classNames('deal-main-deal__subtitle', {
              visible: !ssr,
            })}
          >
            {deal?.title}
          </p>
          <ListMainDeal
            className="highlights"
            list={deal.highlights}
            title={DEAL_SINGLE_SELECT_MODAL.productFeatures}
          />
          <MainDealFullDetails
            collapsed={fullDetailsCollapsed}
            deal={deal}
            onToggle={() => setFullDetailsCollapsed((state) => !state)}
          />
          {!mapView && pins && pins.length > 0 && (
            <>
              <Element name={SCROLL_CONSTANTS.SCROLL_TO_MAP_LOCATION} />
              <SectionTitle title={DEAL_SINGLE_SELECT_MODAL.location} />
              {!isLightPage() && (
                <WowMap
                  maxZoom={15}
                  pins={pins}
                  zoom={deal?.mapZoomDefaultSize}
                />
              )}
            </>
          )}
          <MainDealFinePrint
            collapsed={finePrintCollapsed}
            deal={deal}
            onToggle={() => setFinePrintCollapsed((state) => !state)}
          />
          <PayAmount />
          <div className="deal-main-deal__vat-text--mobile">
            <VatText deal={deal} />
          </div>
          <ProceedButton />
          {getGiftButton()}
        </>
      )}

      <style jsx>{`
        .cyo-header {
          background-color: ${theme.colors.primary};
          min-height: 50px;
          display: flex;
          flex-direction: column;
          justify-content: space-around;
          text-align: center;
          font-size: 20px;
          color: ${theme.colors.textonprimary};
          text-transform: capitalize;
        }
        .deal-headline {
          width: 80%;
        }
        deal-main-deal__subtitle {
          width: 100%;
        }
        .money-item {
          line-height: 1em;
          margin-bottom: 0;
          min-width: 35px;
          display: flex;
          gap: 0.5rem;
          flex-direction: column;
        }
        .label {
          text-transform: uppercase;
          color: black;
          font-size: 12px;
        }
        .deal-main-deal__bought {
          'text-align: center';
          color: ${theme.colors.primary};
          font-weight: bold;
          padding: 8px 0;
          margin: 0;
          font-size: 20px;
        }
        .value-strike {
          font-size: 20px;
          color: ${theme.colors.primary};
          padding: 0 4px;
          margin-left: -4px;
          background: linear-gradient(
            to left top,
            transparent 47.75%,
            currentColor 49.5%,
            currentColor 50.5%,
            transparent 52.25%
          );
        }
        .deal-info {
          padding: 10px 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-grow: 1;
          width: 40%;
          text-decoration: none;
        }
        .value {
          font-size: 20px;
          color: ${theme.colors.primary};
        }
        .cyo-body {
          display: flex;
          flex-direction: column;
          border: 4px solid ${theme.colors.primary};
          border-top: none;
          padding: 20px;
          margin-bottom: 0;
        }
        .cyo-buttons {
          padding-top: 10px;
        }
        .cyo-block {
          margin-bottom: 20px;
        }
        :global(.cyo-deposit) {
          display: flex;
          padding: 5px 0px 10px 0px;
        }
        @media (max-width: ${theme.breakpoints.smDown}) {
          :global(.cyo-deposit) {
            padding-bottom: 10px;
          }
          .deal-info {
            width: 50%;
          }
        }
        :global(.cyo-deposit label) {
          flex-grow: 1;
        }
        :global(.cyo-deposit label .label-text),
        :global(.cyo-deposit label .label-price) {
          margin: 0px;
        }
        :global(.cyo-deposit label:first-child) {
          margin-right: 10px;
        }

        :global(.choose-your-options__cue) {
          overflow: hidden;
        }
        @media (max-width: ${theme.breakpoints.smDown}) {
          :global(.choose-your-options__cue) {
            padding-top: 10px;
          }
        }
        :global(.choose-your-options__discount) {
          float: right;
          padding: 0 0 10px 10px;
        }
        .choose-your-options__image-container {
          padding: 0px 0px 20px 0px;
          position: relative;
          margin-top: -10px;
        }
        :global(.choose-your-options__image-container .thumbs) {
          display: none;
        }
        .choose-your-options__image {
          width: 100%;
        }
        .choose-your-options__inputs {
          padding: 0px 0px 12px 0px;
        }
        :global(.deal-product-selector__empty-message) {
          margin: 0 auto 20px;
          padding: 6px 12px;
          color: ${theme.commoncolors.alert};
          border: 4px solid ${theme.commoncolors.alert};
          border-radius: 4px;
          text-align: center;
        }
        :global(.deal-product-selector__empty-message.hidden) {
          display: none;
        }
        :global(.choose-your-options .modal-body) {
          padding: 0 20px 20px 20px;
        }
        :global(.choose-your-options__discount .deal-main-deal__discount) {
          background: ${theme.colors.primary};
        }
        :global(.choose-your-options .modal-content) {
          border: 1px solid ${theme.colors.primary};
        }
        :global(.size-larger) {
          position: ${layoutOptions.travel ? 'static' : 'sticky'};
          bottom: 0px;
          z-index: 1001;
        }
      `}</style>
    </>
  );
};

DealSingleSelectModal.propTypes = {
  buttonRef: PropTypes.object,
  deal: PropTypes.object,
  finePrintCollapsed: PropTypes.bool,
  fullDetailsCollapsed: PropTypes.bool,
  handleBuyButtonClick: PropTypes.func,
  layout: PropTypes.string,
  mapView: PropTypes.bool,
  secondCheckout: PropTypes.bool,
  setFinePrintCollapsed: PropTypes.func,
  setFullDetailsCollapsed: PropTypes.func,
};

export default DealSingleSelectModal;
